import { isEmailValidator } from '@currency-one/validators'
import { t9n } from 't9n/t9n'

import { NewsletterError } from 'models/endpoints-respone'
import { ConsentType } from 'models/newsletter'

import { pushDataLayer } from 'utils/data-layer-push'
import { signUpForNewsletter, signUpForCurrencyCommentsNewsletter } from 'services/newsletter-service'
import { getPagesNewsletter } from 'services/wp-api/newsletter'
import { newsletterTokenList } from '../config/newsletter-consent'

enum ErrorType {
  EmptyField = 'emptyField',
  EmailNotValid = 'emailNotValid',
  RequiredField = 'requiredField',
  EmailReserved = 'emailReserved',
  MarkField = 'markField',
  ServerUnknownError = 'serverUnknownError',
}

type ValidatedFormData = {
  email: string
  consents: ConsentType[]
}

enum ErrorPlace {
  Email = 'email',
  Consent = 'consent',
  Server = 'server',
}

const $newsletterSection = document.querySelector<HTMLElement>('[data-target="newsletter"]')
const $successSection = document.querySelector<HTMLElement>('[data-target="newsletter-success"]')
const $emailElement = document.querySelector<HTMLInputElement>('[data-newsletter-email]')
const $consentCheckboxes = document.querySelectorAll<HTMLInputElement>('[data-newsletter-consent]')
const $newsletterSubmitButton = document.querySelector<HTMLButtonElement>('[data-submit-newsletter]')
const $closeSuccessButton = document.querySelector<HTMLButtonElement>('[action-hide-newsletter-success-modal]')
const $disclaimer = document.querySelector<HTMLElement>('[data-disclaimer]')
const $consentInputs = document.querySelectorAll<HTMLInputElement>('[data-newsletter-consent]')
const $elementsToErrorMessages = document.querySelectorAll<HTMLElement>('[data-error-field]')
const $consentError = document.querySelector<HTMLElement>('[data-error-field="consent"]')

const removeErrorMessages = (): void => {
  $elementsToErrorMessages.forEach(($error) => ($error.hidden = true))
  if ($emailElement) $emailElement.classList.remove('is-error')
}

const showError = (errorType: ErrorType, errorPlace: ErrorPlace): void => {
  const $errorElement = document.querySelector<HTMLElement>(`[data-error-field="${errorPlace}"]`)
  if ($errorElement) {
    $errorElement.hidden = false
    $errorElement.textContent = t9n(`formValidation.${errorType}`)
  }

  if (errorPlace === ErrorPlace.Email && $emailElement) {
    $emailElement.classList.add('is-error')
  }
}

const resetForm = (): void => {
  if ($emailElement) $emailElement.value = ''
  $consentCheckboxes.forEach((checkbox) => {
    checkbox.checked = false
    const consentType = checkbox.dataset.newsletterConsent
    if (!consentType) return

    const consentText = document.querySelector<HTMLParagraphElement>(`[data-consent-text="${consentType}"]`)
    if (consentText) consentText.hidden = true
  })
  removeErrorMessages()
}

const validateForm = (): ValidatedFormData | null => {
  const email = $emailElement ? $emailElement.value.trim() : ''
  if (email.length <= 0) return showError(ErrorType.EmptyField, ErrorPlace.Email), null
  if (!isEmailValidator(email)) return showError(ErrorType.EmailNotValid, ErrorPlace.Email), null

  const selectedConsents = Array.from($consentCheckboxes)
    .filter((checkbox) => checkbox.checked)
    .map((checkbox) => checkbox.dataset.newsletterConsent as ConsentType)

  if (selectedConsents.length === 0) return showError(ErrorType.MarkField, ErrorPlace.Consent), null

  return { email, consents: selectedConsents }
}

const createThankYouRedirectUrl = (
  thankYouPage: string | undefined,
  successes: ConsentType[],
  failures: ConsentType[] = [],
): string => {
  const thankYouPageUrl = thankYouPage || '#'
  if (!successes || successes.length === 0) return thankYouPageUrl

  const codesSuccess = successes.map((s) => newsletterTokenList[s]).filter(Boolean)
  const codesFail = failures.map((f) => newsletterTokenList[f]).filter(Boolean)

  if (codesSuccess.length === 0) return thankYouPageUrl

  const hasQuery = thankYouPageUrl.includes('?')
  const querySeparator = hasQuery ? '&' : '?'
  const params: string[] = []

  params.push(`newsletter_consent_success=${codesSuccess.join('-')}`)

  if (codesFail.length > 0) {
    params.push(`newsletter_consent_fail=${codesFail.join('-')}`)
  }

  return `${thankYouPageUrl}${querySeparator}${params.join('&')}`
}

const handleApiSuccess = async (successes: ConsentType[], failures: ConsentType[] = []): Promise<void> => {
  pushDataLayer('form_submitted', { form_name: 'Zapis do Newslettera' })
  resetForm()

  try {
    const { thank_you_page } = await getPagesNewsletter()
    const redirect = createThankYouRedirectUrl(thank_you_page, successes, failures)
    window.location.assign(redirect)
  } catch (err) {
    console.error(err)
    const redirect = createThankYouRedirectUrl('#', successes, failures)
    window.location.assign(redirect)
  }
}

const handleApiError = (error: NewsletterError): void => {
  const { data } = error.response
  switch (data) {
    case 'ALREADY_SUBSCRIBED':
      showError(ErrorType.EmailReserved, ErrorPlace.Email)
      break
    case 'INVALID_EMAIL':
      showError(ErrorType.EmailNotValid, ErrorPlace.Email)
      break
    case 'INVALID_LIST':
      showError(ErrorType.ServerUnknownError, ErrorPlace.Server)
      console.warn('Invalid list', error)
      break
    default:
      showError(ErrorType.ServerUnknownError, ErrorPlace.Server)
      console.error('Unexpected error:', error)
      break
  }
}

const submitNewsletterForm = (): void => {
  const formData = validateForm()
  if (!formData) return

  const labeledCalls = formData.consents.map((consentType) => {
    const promise =
      consentType === 'currencyComments'
        ? signUpForCurrencyCommentsNewsletter({ email: formData.email })
        : signUpForNewsletter({ email: formData.email })

    return { consentType, promise }
  })

  Promise.allSettled(labeledCalls.map((c) => c.promise)).then((results) => {
    const successes: ConsentType[] = []
    const failures: Array<{ consentType: ConsentType; reason: NewsletterError }> = []

    results.forEach((res, i) => {
      const consentType = labeledCalls[i].consentType
      if (res.status === 'fulfilled') {
        successes.push(consentType)
      } else {
        failures.push({ consentType, reason: res.reason })
      }
    })

    if (successes.length > 0) {
      const failedConsents = failures.map((fail) => fail.consentType)
      handleApiSuccess(successes, failedConsents)
    } else {
      failures.forEach(({ reason }) => {
        handleApiError(reason)
      })
    }
  })
}

const bindConsentToggle = (): void => {
  $consentCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener('change', () => {
      const consentType = checkbox.dataset.newsletterConsent
      if (!consentType) return

      const consentText = document.querySelector<HTMLParagraphElement>(`[data-consent-text="${consentType}"]`)
      if (consentText) consentText.hidden = !checkbox.checked
    })
  })
}

const handleConsentDisclaimerVisibility = (): void => {
  if (!$disclaimer || !$consentInputs.length) return

  const hideConsentError = (): void => {
    if ($consentError) $consentError.hidden = true
  }

  const updateDisclaimerVisibilityBasedOnConsents = (): void => {
    const anyChecked = Array.from($consentInputs).some((input) => input.checked)
    $disclaimer.hidden = !anyChecked
    if (anyChecked) hideConsentError()
  }

  $consentInputs.forEach((input) => {
    input.addEventListener('change', updateDisclaimerVisibilityBasedOnConsents)
  })
}

export const initNewsletter = (): void => {
  if ($newsletterSubmitButton) {
    $newsletterSubmitButton.addEventListener('click', (event) => {
      event.preventDefault()
      removeErrorMessages()
      submitNewsletterForm()
    })
  }

  if ($closeSuccessButton && $newsletterSection && $successSection) {
    $closeSuccessButton.addEventListener('click', () => {
      $successSection.hidden = true
      $newsletterSection.hidden = false
    })
  }

  handleConsentDisclaimerVisibility()
  bindConsentToggle()
}
